import { Component, OnDestroy, OnInit } from '@angular/core';

import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';

import { HelperService, HTTPService, DataService } from 'app/services';
import { workStateTitle } from 'app/constants';

import { Subscription } from 'rxjs';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';

@Component({
  selector: 'ngx-multi-tech-assign-dialog',
  templateUrl: './multi-tech-assign-dialog.component.html',
  styleUrls: ['./multi-tech-assign-dialog.component.scss'],
  providers: [DialogService],
})
export class MultiTechAssignDialogComponent implements OnInit, OnDestroy {
  orderId: string = '';
  serviceId: string = '';
  isProcessing: boolean = false;
  isSubmitting: boolean = false;
  dataSubscription: Subscription;
  order: any = {};
  technicians: any = [];
  currentTech = {};
  techInitials = {};
  disableServices: any[] = ['deferred', 'deferredLocked', 'declined', 'archived'];
  errors: any = {};
  dialogOpenFrom: string = '';
  source: string = '';
  notificationId : string = '';
  currentLang : string = 'en';

  constructor(
    private dataService: DataService,
    private httpService: HTTPService,
    private helperService: HelperService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    const { data } = this.dynamicDialogConfig;
    this.currentLang = data.currentLang || 'en';
    this.orderId = data.orderId;
    this.serviceId = data.serviceId;
    this.notificationId = data.notificationId;
    this.source = data.source
    this.dialogOpenFrom = data.dialogOpenFrom;
    this.getData();
  }

  ngOnDestroy() {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  async getData() {
    try {
      this.isProcessing = true;
      const [technicians, order] = await Promise.all([this.getTechnician(), this.getEstimate()]);
      this.order = order;

      technicians &&
        technicians.map((tech) => {
          if (!tech.isInActive) this.technicians.push(tech);
        });
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isProcessing = false;
    }
  }

  async getEstimate() {
    if (!this.orderId) return;

    try {
      const options = { isMakeSessionEditable: false, browserSessionId: null };

      if (this.dialogOpenFrom === 'kanban') {
        options.browserSessionId = Date.now();
        options.isMakeSessionEditable = true;
        this.helperService.setLockOrderSession(this.helperService.makeOrderSessionToken(this.orderId, options.browserSessionId));
      }

      const order = await this.httpService.getEstimateById(this.orderId, false, false, options);

      if (order.services && order.services.length) {
        order.services.forEach((service: any) => {
          this.currentTech[service._id] = { tech: {}, hTechOne: {}, hTechTwo: {} };

          if (service.tech && service.tech.techId) {
            this.currentTech[service._id].tech = service.tech;
            service.tech.tooltip = `<div class="flex-column font-12 p-1">
                <p class="mb-0 font-600">${service.tech.name}</p>
                <p class="mb-0 text-center">Service <span class="text-lowercase">${workStateTitle[service.status]}</span></p>
              </div>`;
          }

          if (service.hTechOne && service.hTechOne.techId) {
            this.currentTech[service._id].hTechOne = service.hTechOne;
            service.hTechOne.tooltip = `<div class="flex-column font-12 p-1">
                <p class="mb-0 font-600">${service.hTechOne.name}</p>
                <p class="mb-0 text-center">Service <span class="text-lowercase">${workStateTitle[service.status]}</span></p>
              </div>`;
          }

          if (service.hTechTwo && service.hTechTwo.techId) {
            this.currentTech[service._id].hTechTwo = service.hTechTwo;
            service.hTechTwo.tooltip = `<div class="flex-column font-12 p-1">
                <p class="mb-0 font-600">${service.hTechTwo.name}</p>
                <p class="mb-0 text-center">Service <span class="text-lowercase">${workStateTitle[service.status]}</span></p>
              </div>`;
          }

          service.isActive = !this.disableServices.includes(service.serviceStatus);
        });
      }

      return order;
    } catch (err) {
      this.helperService.showToaster('error', err);
    }
  }

  async getTechnician() {
    try {
      let conf = {
        role: 'tech',
        fetchInActive: false,
      };
      const technicians = await this.httpService.getAllEmployees(conf);

      technicians.forEach((tech: any) => {
        this.techInitials[tech._id] = { name: `${tech.firstName} ${tech.lastName}`, initials: tech.initials };
        tech.isActive = true;
        tech.techId = tech._id;
        tech.name = `${tech.firstName} ${tech.lastName}`;
      });
      return [{ techId: 'unassigned', name: 'Unassigned', isActive: true }, ...technicians];
    } catch (err) {
      this.helperService.showToaster('error', err);
    }
  }

  setDataListener() {
    this.dataSubscription = this.dataService.getData().subscribe(({ data }) => {
      if (data) {
        if (data.key === 'company-appdata-updated') {
          this.dataSubscription.unsubscribe();
        }
      }
    });
  }

  handleReassignDialog({ oTech = {}, nTech = {}, nRole = '', oRole = '', serviceId }: any) {
    const role = {
      tech: 'Primary tech',
      hTechOne: 'Helper 1 tech',
      hTechTwo: 'Helper 2 tech',
    };
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Reassign ${oTech.name} as ${role[oRole]}`,
        message: `We have assigned ${nTech.name} as the ${role[nRole]}. Do you want to reassign ${oTech.name} (former ${role[nRole]}) as ${role[oRole]}? `,
        button: {
          success: { title: 'Reassign' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (data && data.btn === 'close') return;

      if (isSuccess) {
        this.handleTechSelect({ serviceId, tech: nTech, key: nRole, unAssignTechFromRole: false, isCheckForReassignments: false });
        this.handleTechSelect({ serviceId, tech: oTech, key: oRole, unAssignTechFromRole: false, isCheckForReassignments: false });
      } else {
        this.handleTechSelect({ serviceId, tech: nTech, key: nRole, unAssignTechFromRole: true, isCheckForReassignments: false });
      }

      onClose.unsubscribe();
    });
  }

  handleSelectMultiple(key: string, value: any) {
    this.order.services.forEach((service: any) => {
      const { _id: serviceId } = service;
      const currentTech = this.currentTech[serviceId];

      if (!service.tech) service.tech = {};
      if (!service.hTechOne) service.hTechOne = {};
      if (!service.hTechTwo) service.hTechTwo = {};

      if (service.tech.techId && !service.tech.workStatus) service.tech.workStatus = service.status;

      // If technician service is in progress don't make any changes.
      if ((service[key].techId && service[key].workStatus === 'inprogress') || this.disableServices.includes(service.serviceStatus)) {
        return;
      }

      ['tech', 'hTechOne', 'hTechTwo'].forEach((techKey) => {
        if (key === techKey) {
          this.currentTech[serviceId][techKey] = value.techId === 'unassigned' ? {} : { name: value.name, techId: value.techId, initials: value.initials };
          this.errors[serviceId] = {};
        }
      });

      // Remove duplicate technicians from helpers
      if (key === 'tech') {
        ['hTechOne', 'hTechTwo'].forEach((teckKey) => {
          if (currentTech[teckKey].workStatus !== 'inprogress' && currentTech[teckKey].techId === value.techId) {
            this.currentTech[serviceId][teckKey] = {};
          }
        });
      }

      if (key === 'hTechOne') {
        ['tech', 'hTechTwo'].forEach((teckKey) => {
          if (currentTech[teckKey].workStatus !== 'inprogress' && currentTech[teckKey].techId === value.techId) {
            this.currentTech[serviceId][teckKey] = {};
          }
        });
      }

      if (key === 'hTechTwo') {
        ['tech', 'hTechOne'].forEach((teckKey) => {
          if (currentTech[teckKey].workStatus !== 'inprogress' && currentTech[teckKey].techId === value.techId) {
            this.currentTech[serviceId][teckKey] = {};
          }
        });
      }
    });
  }

  handleTechSelect({ serviceId, key, tech, unAssignTechFromRole = true, isCheckForReassignments = true }) {
    const currectTechs = this.currentTech[serviceId];
    const roles = ['tech', 'hTechOne', 'hTechTwo'];

    // Check if reassignment is needed e.g same technician assign to other role
    if (currectTechs[key].techId && isCheckForReassignments) {
      const otherTechRoles = [];

      // Populating other roles
      roles.forEach((el) => {
        if (el !== key) otherTechRoles.push(el);
      });

      if ([...otherTechRoles.map((k) => currectTechs[k].techId)].includes(tech.techId)) {
        const oRole = currectTechs[otherTechRoles[0]].techId && tech.techId === currectTechs[otherTechRoles[0]].techId ? otherTechRoles[0] : otherTechRoles[1];
        const data = {
          oRole,
          serviceId,
          nRole: key,
          nTech: { ...tech },
          oTech: { ...currectTechs[key] },
        };
        this.handleReassignDialog(data);
        return;
      }
    }

    this.currentTech[serviceId][key] = tech.techId === 'unassigned' ? {} : { name: tech.name, techId: tech.techId, initials: tech.initials, rate: tech.rate };
    this.errors[serviceId] = {};

    if (unAssignTechFromRole) {
      const otherRoles = roles.filter((r) => r !== key);
      otherRoles.forEach((otherRole) => {
        if ([currectTechs[otherRole].techId].includes(tech.techId) && currectTechs[otherRole].workStatus !== 'inprogress') {
          this.currentTech[serviceId][otherRole] = {};
        }
      });
    }

    this.currentTech[serviceId] = { ...this.currentTech[serviceId] };
  }

  handleClose(isSuccess: boolean = false, data: any = {}) {
    this.dynamicDialogRef.close({ isSuccess, data });

    if (this.dialogOpenFrom === 'kanban') {
      this.httpService
        .updateOrderRestriction(this.order._id, { status: 'readOnly' })
        .then((res) => {})
        .catch((err) => {
          console.log(err);
        });
    }
  }

  isAssignmentsValid() {
    let isValid = true;
    let isHelperMissing = false;
    let isPrimaryMissing = false;

    Object.keys(this.currentTech).forEach((serviceId) => {
      this.errors[serviceId] = {};

      if (this.currentTech[serviceId].tech.techId || this.currentTech[serviceId].hTechOne.techId || this.currentTech[serviceId].hTechTwo.techId) {
        if (this.currentTech[serviceId].hTechTwo.techId) {
          if (!this.currentTech[serviceId].hTechOne.techId) {
            this.errors[serviceId].hTechOne = 'Not assigned';
            isHelperMissing = true;
            isValid = false;
          }

          if (!this.currentTech[serviceId].tech.techId) {
            this.errors[serviceId].tech = 'Not assigned';
            isPrimaryMissing = true;
            isValid = false;
          }
        }

        if (this.currentTech[serviceId].hTechOne.techId) {
          if (!this.currentTech[serviceId].tech.techId) {
            isPrimaryMissing = true;
            this.errors[serviceId].tech = 'Not assigned';
            isValid = false;
          }
        }
      }
    });

    if (!isValid) {
      let message = '';
      if (isPrimaryMissing && isHelperMissing) {
        message = 'Assign Primary tech and Helper 1 to missing services before saving';
      } else if (isPrimaryMissing) {
        message = 'Assign Primary tech to missing services before saving.';
      } else if (isHelperMissing) {
        message = 'Assign Helper 1 to missing services before saving.';
      }

      this.helperService.showToaster('error', message);
    }

    return isValid;
  }

  handleSubmit() {
    if (!this.isAssignmentsValid()) return;

    const techAssignments = {};

    this.order.services.forEach((service: any) => {
      const { tech = {}, hTechOne = {}, hTechTwo = {}, _id: serviceId } = service;
      const currectTechs = this.currentTech[serviceId];

      techAssignments[serviceId] = {};

      if (tech?.techId !== currectTechs?.tech?.techId) {
        if (currectTechs.tech && currectTechs.tech.techId) {
          techAssignments[serviceId].tech = {
            name: currectTechs.tech.name,
            role: 'primary',
            techId: currectTechs.tech.techId,
            initials: currectTechs.tech.initials,
            rate: currectTechs.tech.rate,
            registrationNumber: this.technicians && this.technicians.find((technician) => currectTechs.tech.techId === technician._id).registrationNumber,
          };
        } else {
          techAssignments[serviceId].tech = { techId: 'unassigned' };
        }
      }

      if (hTechOne?.techId !== currectTechs?.hTechOne?.techId) {
        if (currectTechs.hTechOne && currectTechs.hTechOne.techId) {
          techAssignments[serviceId].hTechOne = {
            name: currectTechs.hTechOne.name,
            role: 'helper',
            techId: currectTechs.hTechOne.techId,
            initials: currectTechs.hTechOne.initials,
            rate: currectTechs.hTechOne.rate,
            registrationNumber: this.technicians && this.technicians.find((technician) => currectTechs.hTechOne.techId === technician._id).registrationNumber,
          };
        } else {
          techAssignments[serviceId].hTechOne = { techId: 'unassigned' };
        }
      }

      if (hTechTwo?.techId !== currectTechs?.hTechTwo?.techId) {
        if (currectTechs.hTechTwo && currectTechs.hTechTwo.techId) {
          techAssignments[serviceId].hTechTwo = {
            name: currectTechs.hTechTwo.name,
            role: 'helper',
            techId: currectTechs.hTechTwo.techId,
            initials: currectTechs.hTechTwo.initials,
            rate: currectTechs.hTechTwo.rate,
            registrationNumber: this.technicians && this.technicians.find((technician) => currectTechs.hTechTwo.techId === technician._id).registrationNumber,
          };
        } else {
          techAssignments[serviceId].hTechTwo = { techId: 'unassigned' };
        }
      }

      if (!Object.keys(techAssignments[serviceId]).length) {
        delete techAssignments[serviceId];
      }
    });

    this.isSubmitting = true;
    this.httpService
      .bulkAssignTechnician(this.order._id, techAssignments ,{source: this.source, notificationId: this.notificationId} )
      .then(
        (res) => {
          this.handleClose(true);
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isSubmitting = false;
      });
  }
}
